// client/src/main.tsx
// React application entry point for AGOC School.
//
// Provider order (outermost → innermost) matters:
//   QueryClientProvider — React Query cache, must be outermost
//   AuthProvider        — Firebase Auth state + JWT claim extraction; must be
//                         established BEFORE TenantProvider so TenantContext
//                         can read the user token and call /tenant-config
//   TenantProvider      — fetches /tenant-config once auth is ready
//   IonApp              — Ionic UI root (wraps everything Ionic touches)
//
// Router:
//   @ionic/react-router uses React Router v5 internally.
//   - IonReactRouter    wraps BrowserRouter (web / PWA)
//   - IonReactMemoryRouter wraps MemoryRouter (Capacitor native — no URL bar)
//   isPlatform('hybrid') returns true on iOS/Android via Capacitor.
//   The router must be the DIRECT parent of IonRouterOutlet in App.tsx.

import React from 'react';
import ReactDOM from 'react-dom/client';

import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { IonApp, isPlatform, setupIonicReact } from '@ionic/react';
import { IonReactRouter, IonReactMemoryRouter } from '@ionic/react-router';
import { createMemoryHistory, MemoryHistory } from 'history';

import { AuthProvider } from '@/contexts/AuthContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import App from '@/App';

// Ionic CSS and Tailwind are imported in index.css (single source of truth)
import '@/index.css';

// Configure Ionic defaults (must be called before any Ionic component is rendered)
setupIonicReact({
  mode: 'md', // Material Design across all platforms for visual consistency
});

// Select the correct Ionic router for the runtime environment.
// IonReactMemoryRouter is REQUIRED for Capacitor native (no URL bar on device).
// IonReactRouter (BrowserRouter) is used for web and PWA.
const memoryHistory: MemoryHistory = createMemoryHistory();
const isNative = isPlatform('hybrid');

const root = document.getElementById('root');
if (!root) throw new Error('[main] #root element not found in index.html');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/*
          TenantProvider is inside AuthProvider so it can call useAuth() to
          wait for a valid user token before fetching /tenant-config.
        */}
        <TenantProvider>
          {isNative ? (
            <IonReactMemoryRouter history={memoryHistory}>
              <IonApp>
                <TooltipProvider>
                  <App />
                  <Toaster />
                </TooltipProvider>
              </IonApp>
            </IonReactMemoryRouter>
          ) : (
            <IonReactRouter>
              <IonApp>
                <TooltipProvider>
                  <App />
                  <Toaster />
                </TooltipProvider>
              </IonApp>
            </IonReactRouter>
          )}
        </TenantProvider>
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
